declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
  }
}

/**
 * GA4 event tracking helpers.
 *
 * All custom event calls go through this module so event names and
 * parameters are defined in one place. The gtag global is injected by
 * @next/third-parties <GoogleAnalytics> component.
 */

/* ------------------------------------------------------------------ */
/*  Type-safe gtag wrapper                                            */
/* ------------------------------------------------------------------ */

type GtagEvent = {
  action: string;
  category?: string;
  label?: string;
  value?: number;
  [key: string]: string | number | undefined;
};

function sendEvent({ action, category, label, value, ...rest }: GtagEvent) {
  if (typeof window === "undefined" || typeof window.gtag !== "function") {
    return;
  }
  window.gtag("event", action, {
    event_category: category,
    event_label: label,
    value,
    ...rest,
  });
}

/* ------------------------------------------------------------------ */
/*  CTA click events                                                  */
/* ------------------------------------------------------------------ */

/**
 * Track a CTA button click.
 * @param ctaName - Human-readable name, e.g. "hero_download", "navbar_download"
 * @param location - Section of the page, e.g. "hero", "navbar", "final_cta"
 */
export function trackCTAClick(ctaName: string, location: string) {
  sendEvent({
    action: "cta_click",
    category: "engagement",
    label: ctaName,
    cta_location: location,
  });
}

/* ------------------------------------------------------------------ */
/*  Store link click events                                           */
/* ------------------------------------------------------------------ */

/**
 * Track a click on a Play Store or App Store link.
 * @param store - "play_store" or "app_store"
 * @param location - Section of the page where the link was clicked
 */
export function trackStoreClick(
  store: "play_store" | "app_store",
  location: string,
) {
  sendEvent({
    action: "store_click",
    category: "conversion",
    label: store,
    store_type: store,
    click_location: location,
  });
}

/* ------------------------------------------------------------------ */
/*  Scroll depth events                                               */
/* ------------------------------------------------------------------ */

/**
 * Track scroll depth milestones.
 * @param percent - 25, 50, 75, or 100
 */
export function trackScrollDepth(percent: 25 | 50 | 75 | 100) {
  sendEvent({
    action: "scroll_depth",
    category: "engagement",
    label: `${percent}%`,
    value: percent,
    depth_percent: percent,
  });
}

/* ------------------------------------------------------------------ */
/*  FAQ interaction events                                            */
/* ------------------------------------------------------------------ */

/**
 * Track when a user views/interacts with a FAQ item.
 * @param questionIndex - 0-based index of the FAQ item
 * @param questionText - The question text (truncated for GA4 limits)
 */
export function trackFAQInteraction(
  questionIndex: number,
  questionText: string,
) {
  sendEvent({
    action: "faq_interaction",
    category: "engagement",
    label: questionText.slice(0, 100),
    value: questionIndex,
    faq_index: questionIndex,
  });
}
